import React, { useMemo, useState } from 'react'
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Chip } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import CustomTooltip from '../../../../../components/CustomTooltip'
import { formatDateByUserTimeZone } from '../../../../../helpers/formatDateByUserTimeZone'
import { SlotStatus } from '../../../../../models/DistributionPlan'
import { chipColors } from '../../../../ProjectCourseFeature/CourseEvaluationHeader'
import { blue, grey } from '../../../../../colors'
import ROUTES from '../../../../../consts/paths'
import { capitalizeFirst } from '../../../../../utils/utils'
import { viewFromStatus } from '../../../../ProjectCourseFeature/ProjectSectionTab'
import GenericButton from '../../../../../components/GenericButton'
import { ButtonType } from '../../../../../components/GenericButton/styles'
import AddToSurveyProjectModal from './AddToSurveyProjectModal'
import { type ISection } from '../../../../../models/TermsSection'
import { READONLY_TOOLTIP } from '../../../../../consts'

interface ProjectTabPanelProps {
  selectedRow?: ISection | Record<string, any>
  termId: string
  readonly?: boolean
  onRefresh: () => Promise<void>
  onParentClose: () => void
}

export const ProjectTabPanel: React.FC<ProjectTabPanelProps> = ({
  selectedRow,
  termId,
  readonly = false,
  onRefresh,
  onParentClose
}) => {
  const [isAddToProjectModalOpen, setIsAddToProjectModalOpen] = useState(false)

  const linkedProjects = useMemo(() => selectedRow?.distributionDetails ?? [], [selectedRow?.distributionDetails])

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <CustomTooltip title={readonly ? READONLY_TOOLTIP : ''} disableHoverListener={!readonly}>
        <Box mb={2}>
          <GenericButton
            label="Add to Survey Project"
            fullWidth
            buttonType={ButtonType.Secondary}
            onClick={() => { setIsAddToProjectModalOpen(true) }}
            disabled={readonly}
            sx={{
              backgroundColor: grey[0],
              border: `1px solid ${grey[300]}`
            }}
          />
        </Box>
      </CustomTooltip>
      {
        !linkedProjects.length
          ? <Typography fontWeight={'600'} mb={1}>This section is not linked to any survey projects.</Typography>
          : <>
            <Typography fontWeight={'600'} mb={1}>This section is linked to the following survey projects:</Typography>
            {
              linkedProjects.map((project: any) => {
                const formattedStart = project?.startTimestamp && formatDateByUserTimeZone(project.startTimestamp)
                const formattedEnd = project?.endTimestamp && formatDateByUserTimeZone(project.endTimestamp)
                const slotKey = String(project?.slotId ?? project?.slotTitle)
                const chipVariant = project?.slotStatus === SlotStatus.Draft ? 'outlined' : 'filled'
                const chipColor: any = chipColors[typeof project?.slotStatus === 'string' ? project.slotStatus : 'Draft']

                return (
                  <Accordion key={slotKey} sx={{ boxShadow: 'none' }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel-${slotKey}-content`}
                      id={`panel-${slotKey}-header`}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: grey[100] },
                        '&.Mui-expanded': { backgroundColor: grey[100], borderRadius: 1 }
                      }}
                    >
                      <Typography fontWeight="bold" fontSize={16}>{capitalizeFirst(project?.slotTitle ?? '')}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1, my: 2, ml: 1 }}>
                      <Box display='flex' justifyContent={'space-between'} alignItems='center'>
                        <Box display={'flex'} gap={2}>
                          <Typography fontWeight={500} color={grey[600]}>Survey Status:</Typography>
                          <Chip variant={chipVariant} size='small' color={chipColor} label={project?.slotStatus} />
                        </Box>

                        <CustomTooltip title="Go to Survey Project Section">
                          <Typography
                            fontWeight="bold"
                            fontSize={16}
                            sx={{ cursor: 'pointer' }}
                            onClick={e => {
                              e.stopPropagation()
                              window.open(`${ROUTES.COURSE_AND_ENROLLMENT}/${project?.slotId}?search=${selectedRow?.abbreviation}&view=${viewFromStatus(project?.slotStatus)}`, '_blank')
                            }}
                          >
                            <OpenInNewOutlinedIcon sx={{ color: blue[500] }} />
                          </Typography>
                        </CustomTooltip>
                      </Box>
                      <Box display='flex' gap={2}>
                        <Typography fontWeight={500} color={grey[600]}>Survey Start:</Typography>
                        <Typography fontWeight={500}>{formattedStart}</Typography>
                      </Box>
                      <Box display='flex' gap={2}>
                        <Typography fontWeight={500} color={grey[600]}>Survey End:</Typography>
                        <Typography fontWeight={500}>{formattedEnd}</Typography>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )
              })
            }
          </>
      }
      <AddToSurveyProjectModal
        isOpen={isAddToProjectModalOpen}
        onClose={() => { setIsAddToProjectModalOpen(false) }}
        selectedRow={selectedRow}
        termId={termId}
        readonly={readonly}
        onRefresh={onRefresh}
        onParentClose={onParentClose}
      />
    </Box>
  )
}
